import { useState, useEffect } from "react";
import {
  createFileRoute,
  useParams,
  useNavigate,
  Outlet,
} from "@tanstack/react-router";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { toast } from "sonner";

import { ProtectedRoute } from "@/components/protected-route";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { View, SS_Button } from "@/components/native-wrapper-ui";
import HeaderView from "@/components/headerView";

import JobInfoPanelView from '@/components/jobs/job-info-panel';

import { useAuth } from "@/contexts/auth-context";
import { getBookings } from "@/store/slices/bookingsSlice";
import { selectJobOverviewData, selectBookingByJobIdAndDate, selectCheckinByBooking } from "@/store/selectors";

import {
  updateHelpModal,
  updateNotesModal,
} from "@/store/slices/appStateSlice";

export const Route = createFileRoute("/job/$jobId/$date/")({
  component: () => (
    <ProtectedRoute>
      <JobOverviewPage />
    </ProtectedRoute>
  ),
});

interface JobTile {
  id: string;
  name: string;
  icon: SVGElement;
  active: boolean;
  incomplete: boolean;
  onPress: () => void;
  style?: string;
  textStyle?: string;
}

function JobOverviewPage() {
  const { jobId, date } = useParams({ from: "/job/$jobId/$date" });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();

  // Use Redux selector to get all job-related data - date-aware booking lookup
  const { job, jobState } = useSelector((state: any) =>
    selectJobOverviewData(state, jobId)
  );
  const booking = useSelector((state: any) =>
    selectBookingByJobIdAndDate(state, jobId, date)
  );

  const checkinToday = useSelector((state: any) =>
    booking ? selectCheckinByBooking(state, booking.id, booking.date) : null
  );

  // Load data if needed
  useEffect(() => {
    if (!booking && user) {
      dispatch(getBookings({ page: 0 }));
    }
  }, [booking, user, dispatch]);

  const goBack = () => {
    navigate({ to: "/" });
  };

  const showNotesModal = () => {
    toast.info("Notes modal functionality needs to be implemented");
  };

  // Job workflow tiles configuration
  const jobTiles: JobTile[] = [
    {
      id: "tasks",
      name: "Tasks / Notes",
      icon: (
        <svg
          className="fill-current w-16 h-16"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
        >
          <path d="M153.8 72.1c8.9-9.9 8.1-25-1.8-33.9s-25-8.1-33.9 1.8L63.1 101.1 41 79C31.6 69.7 16.4 69.7 7 79s-9.4 24.6 0 33.9l40 40c4.7 4.7 11 7.2 17.6 7s12.8-3 17.2-7.9l72-80zm0 160c8.9-9.9 8.1-25-1.8-33.9s-25-8.1-33.9 1.8L63.1 261.1 41 239c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l40 40c4.7 4.7 11 7.2 17.6 7s12.8-3 17.2-7.9l72-80zM216 120l272 0c13.3 0 24-10.7 24-24s-10.7-24-24-24L216 72c-13.3 0-24 10.7-24 24s10.7 24 24 24zM192 256c0 13.3 10.7 24 24 24l272 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-272 0c-13.3 0-24 10.7-24 24zM160 416c0 13.3 10.7 24 24 24l304 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-304 0c-13.3 0-24 10.7-24 24zm-64 0a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z" />
        </svg>
      ),
      active: true,
      incomplete: !jobState ||
                      (jobState &&
                        (!jobState.confirmed_tasks ||
                          jobState.confirmed_tasks === null)),
      onPress: () => navigate({ to: '/job/$jobId/$date/tasks', params: { jobId, date } }),
    },
    {
      id: "materials",
      name: "Materials",
      icon: (
        <svg
          className="fill-current w-16 h-16"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
        >
          <path d="M64.7 34.6L160 107.9l0 52.1-52.1 0L34.6 64.7 64.7 34.6zM192 169.3c0-.4 0-.9 0-1.3l0-64c0-7.5-3.5-14.5-9.4-19L78.6 5C69.1-2.4 55.6-1.5 47 7L7 47c-8.5 8.5-9.4 22-2.1 31.6l80 104c4.5 5.9 11.6 9.4 19 9.4l64 0c.4 0 .9 0 1.3 0L271.2 293.9c-19.5 30-16.2 70.5 10.1 96.8l112 112c12.5 12.5 32.8 12.5 45.3 0l64-64c12.5-12.5 12.5-32.8 0-45.3l-112-112c-26.3-26.3-66.8-29.7-96.8-10.1L192 169.3zM304 304c17.7-17.7 46.3-17.7 64 0L480 416l-64 64L304 368c-17.7-17.7-17.7-46.3 0-64zM104 424a16 16 0 1 0 -32 0 16 16 0 1 0 32 0zM23.2 376.8C8.3 391.7 0 411.8 0 432.8C0 476.5 35.5 512 79.2 512c21 0 41.1-8.3 56-23.2L238.5 385.5c-5.7-11.2-9.3-23.1-10.9-35.3L112.1 465.7c-8.7 8.7-20.6 13.6-32.9 13.6c-25.7 0-46.6-20.8-46.6-46.6c0-12.3 4.9-24.2 13.6-32.9L189.1 257l-23.1-23.1L23.2 376.8zM479.4 160c0 44.8-23.1 84.1-58 106.8l23.5 23.5c40.7-29 67.2-76.6 67.2-130.3c0-24.8-5.7-48.3-15.7-69.3c-4.4-9.2-16.5-10.5-23.7-3.3l-67.9 67.9c-3 3-7.1 4.7-11.3 4.7L368 160c-8.8 0-16-7.2-16-16l0-25.4c0-4.2 1.7-8.3 4.7-11.3l67.9-67.9c7.2-7.2 5.9-19.3-3.3-23.7C400.3 5.7 376.8 0 352 0C296.1 0 246.9 28.6 218.3 72.1l26 20C266.8 56.3 306.7 32.6 352 32.6c10.3 0 20.2 1.2 29.7 3.5L333.6 84.2c-9.1 9.1-14.2 21.5-14.2 34.4l0 25.4c0 26.9 21.8 48.6 48.6 48.6l25.4 0c12.9 0 25.3-5.1 34.4-14.2l48.1-48.1c2.3 9.5 3.5 19.5 3.5 29.7z" />
        </svg>
      ),
      active: jobState &&
                      jobState.confirmed_tasks &&
                      jobState.confirmed_tasks !== null,
      incomplete: jobState &&
                      (!jobState.confirmed_materials ||
                        jobState.confirmed_materials === null),
      onPress: () => navigate({ to: '/job/$jobId/$date/materials', params: { jobId, date } }),
    },
    {
      id: "checkin",
      name: "Check In",
      icon: (
        <svg
          className="fill-current w-16 h-16"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 576 512"
        >
          <path d="M366.6 155c6.7-15.9 9.4-27.6 9.4-35c0-48.6-39.4-88-88-88s-88 39.4-88 88c0 7.4 2.7 19 9.4 35c6.5 15.4 15.7 32.4 26.4 49.7c17.1 27.8 36.9 54.7 52.2 74.4c15.3-19.7 35.1-46.6 52.2-74.4c10.7-17.3 19.9-34.3 26.4-49.7zM302.8 312c-7.7 9.6-22 9.6-29.6 0C241.1 271.9 168 174.6 168 120C168 53.7 221.7 0 288 0s120 53.7 120 120c0 54.6-73.1 151.9-105.2 192zm103.1-89.1c-.8 .3-1.7 .6-2.5 .8c8.2-14.2 15.7-28.7 21.8-42.9l117.9-47.2c15.8-6.3 32.9 5.3 32.9 22.3l0 270.8c0 9.8-6 18.6-15.1 22.3l-155 62c-3.3 1.3-6.9 1.5-10.3 .5L176.9 448.9l-144 57.6C17.1 512.8 0 501.2 0 484.2L0 213.4c0-9.8 6-18.6 15.1-22.3l123.2-49.3c2.1 10.4 5.4 20.8 9.2 30.8L32 218.8l0 253.5 128-51.2L160 304c0-8.8 7.2-16 16-16s16 7.2 16 16l0 115.9 192 54.9L384 304c0-8.8 7.2-16 16-16s16 7.2 16 16l0 168.4 128-51.2 0-253.5L405.9 222.9zM288 88a24 24 0 1 1 0 48 24 24 0 1 1 0-48z" />
        </svg>
      ),
      active: jobState &&
                      jobState.confirmed_materials &&
                      jobState.confirmed_materials !== null,
      incomplete: !checkinToday,
      onPress: () => navigate({ to: '/job/$jobId/$date/checkin', params: { jobId, date } }),
    },
    {
      id: "walkthrough",
      name: "Walkthrough",
      icon: (
        <svg
          className="fill-current w-16 h-16"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 640 512"
        >
          <path d="M323.7 62.6C355.1 54.7 390.3 48 416 48c41.2 0 89.8 10.6 126.5 28.9C582 96.7 592 116.3 592 128c0 9.4-5.1 21.5-35.9 33.1c-29.4 11.1-67 14.9-92.1 14.9c-34.7 0-53.5-10.4-80.2-25.7l-.7-.4c-20.6-11.8-45.1-25.8-79.1-33l0-49.1c6-1.7 12.7-3.4 19.7-5.2zM256 160c48 0 76 16 104 32s56 32 104 32c56.4 0 176-16 176-96S512 0 416 0C352.3 0 256 32 256 32l0 80 0 48s0 0 0 0zM128 96c0 35.3 28.7 64 64 64l32 0 0-128-32 0c-35.3 0-64 28.7-64 64zm67.7 353.4c-7-1.8-13.7-3.5-19.7-5.2l0-49.1c34-7.3 58.5-21.3 79.1-33l.7-.4C282.5 346.4 301.3 336 336 336c25.2 0 62.7 3.7 92.1 14.9C458.9 362.5 464 374.6 464 384c0 11.7-10 31.3-49.5 51.1C377.8 453.4 329.2 464 288 464c-25.7 0-60.9-6.7-92.3-14.6zM128 352s0 0 0 0l0 48 0 80s96.3 32 160 32c96 0 224-48 224-128s-119.6-96-176-96c-48 0-76 16-104 32s-56 32-104 32zM0 416c0 35.3 28.7 64 64 64l32 0 0-128-32 0c-35.3 0-64 28.7-64 64z" />
        </svg>
      ),
      active: !!checkinToday,
      incomplete: jobState &&
                      (!jobState.confirmed_walkthrough ||
                        jobState.confirmed_walkthrough === null),
      onPress: () => navigate({ to: '/job/$jobId/$date/walkthrough', params: { jobId, date } }),
    },
    {
      id: "health_safety",
      name: "Health & Safety",
      icon: (
        <svg
          className="fill-current w-16 h-16"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 448 512"
        >
          <path d="M176 60.8c-28.7 16.6-48 47.6-48 83.2l192 0c0-35.5-19.3-66.6-48-83.2L272 80c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-48-32 0 0 48c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-19.2zM271.2 25C318.5 43.8 352 90 352 144l16 0c8.8 0 16 7.2 16 16s-7.2 16-16 16L80 176c-8.8 0-16-7.2-16-16s7.2-16 16-16l16 0c0-54 33.5-100.2 80.8-119C180 10.7 192.7 0 208 0l32 0c15.3 0 28 10.7 31.2 25zM224 320c-65.3 0-119.1-48.9-127-112l32.3 0c7.6 45.4 47.1 80 94.7 80s87.1-34.6 94.7-80l32.3 0c-7.9 63.1-61.7 112-127 112zM415.8 480c-3.3-53.6-47.8-96-102.2-96l-179.2 0C80 384 35.5 426.4 32.2 480l383.6 0zM0 486.4C0 412.2 60.2 352 134.4 352l89.6 0 89.6 0C387.8 352 448 412.2 448 486.4c0 14.1-11.5 25.6-25.6 25.6L25.6 512C11.5 512 0 500.5 0 486.4z" />
        </svg>
      ),
      active: checkinToday &&
                      jobState &&
                      jobState.confirmed_walkthrough &&
                      jobState.confirmed_walkthrough !== null,
      incomplete: jobState &&
                      (!jobState.confirmed_health_and_safety ||
                        jobState.confirmed_health_and_safety === null),
      onPress: () => navigate({ to: '/job/$jobId/$date/health-safety', params: { jobId, date } }),
    },
    {
      id: "photos",
      name: "Photos",
      icon: (
        <svg
          className="fill-current w-16 h-16"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 576 512"
        >
          <path d="M160 64l352 0c17.7 0 32 14.3 32 32l0 105.4L486.6 144c-12.5-12.5-32.8-12.5-45.3 0L304 281.4 230.6 208c-12.5-12.5-32.8-12.5-45.3 0L128 265.4 128 96c0-17.7 14.3-32 32-32zM576 96c0-35.3-28.7-64-64-64L160 32c-35.3 0-64 28.7-64 64l0 208 0 16c0 35.3 28.7 64 64 64l80 0c0 0 0 0 0 0l272 0c35.3 0 64-28.7 64-64l0-80c0 0 0 0 0-.1l0-144zM464 166.6l80 80 0 73.4c0 17.7-14.3 32-32 32l-233.4 0 36.7-36.7L464 166.6zM281.4 304l-48 48L160 352c-17.7 0-32-14.3-32-32l0-9.4 80-80L281.4 304zM32 112c0-8.8-7.2-16-16-16s-16 7.2-16 16L0 352c0 70.7 57.3 128 128 128l336 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-336 0c-53 0-96-43-96-96l0-240zm232 48a24 24 0 1 0 0-48 24 24 0 1 0 0 48z" />
        </svg>
      ),
      active: checkinToday &&
                      jobState &&
                      jobState.confirmed_health_and_safety &&
                      jobState.confirmed_health_and_safety !== null,
      incomplete: jobState &&
                      (!jobState.confirmed_protective_photos ||
                        jobState.confirmed_protective_photos === null),
      onPress: () => {
        // Navigate to protective-photos if not confirmed, otherwise to photos
        if (!jobState?.confirmed_protective_photos) {
          navigate({ to: '/job/$jobId/$date/protective-photos', params: { jobId, date } });
        } else {
          navigate({ to: '/job/$jobId/$date/photos', params: { jobId, date } });
        }
      },
    },
    {
      id: "checkout",
      name: "Check Out",
      icon: (
        <svg
          className="fill-current w-16 h-16"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 576 512"
        >
          <path d="M366.6 155c6.7-15.9 9.4-27.6 9.4-35c0-48.6-39.4-88-88-88s-88 39.4-88 88c0 7.4 2.7 19 9.4 35c6.5 15.4 15.7 32.4 26.4 49.7c17.1 27.8 36.9 54.7 52.2 74.4c15.3-19.7 35.1-46.6 52.2-74.4c10.7-17.3 19.9-34.3 26.4-49.7zM302.8 312c-7.7 9.6-22 9.6-29.6 0C241.1 271.9 168 174.6 168 120C168 53.7 221.7 0 288 0s120 53.7 120 120c0 54.6-73.1 151.9-105.2 192zm103.1-89.1c-.8 .3-1.7 .6-2.5 .8c8.2-14.2 15.7-28.7 21.8-42.9l117.9-47.2c15.8-6.3 32.9 5.3 32.9 22.3l0 270.8c0 9.8-6 18.6-15.1 22.3l-155 62c-3.3 1.3-6.9 1.5-10.3 .5L176.9 448.9l-144 57.6C17.1 512.8 0 501.2 0 484.2L0 213.4c0-9.8 6-18.6 15.1-22.3l123.2-49.3c2.1 10.4 5.4 20.8 9.2 30.8L32 218.8l0 253.5 128-51.2L160 304c0-8.8 7.2-16 16-16s16 7.2 16 16l0 115.9 192 54.9L384 304c0-8.8 7.2-16 16-16s16 7.2 16 16l0 168.4 128-51.2 0-253.5L405.9 222.9zM288 88a24 24 0 1 1 0 48 24 24 0 1 1 0-48z" />
        </svg>
      ),
      active: checkinToday && jobState?.confirmed_protective_photos !== null,
      incomplete: !checkinToday || !checkinToday.checked_out_at,
      onPress: () => navigate({ to: '/job/$jobId/$date/checkout', params: { jobId, date } }),
    },
    {
      id: "signoff",
      name: "Client Sign Off",
      icon: (
        <svg
          className="fill-current w-16 h-16"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 576 512"
        >
          <path d="M320 480c17.7 0 32-14.3 32-32l0-10.7 23.8-5.9c2.8-.7 5.6-1.6 8.2-2.7l0 19.3c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 64C0 28.7 28.7 0 64 0L220.1 0c12.7 0 24.9 5.1 33.9 14.1L369.9 129.9c9 9 14.1 21.2 14.1 33.9l0 39.8-32 32 0-43.6-112 0c-26.5 0-48-21.5-48-48l0-112L64 32C46.3 32 32 46.3 32 64l0 384c0 17.7 14.3 32 32 32l256 0zM240 160l111.5 0c-.7-2.8-2.1-5.4-4.2-7.4L231.4 36.7c-2.1-2.1-4.6-3.5-7.4-4.2L224 144c0 8.8 7.2 16 16 16zM144 349l-9.8 32.8c-6.1 20.3-24.8 34.2-46 34.2L80 416c-8.8 0-16-7.2-16-16s7.2-16 16-16l8.2 0c7.1 0 13.3-4.6 15.3-11.4l14.9-49.5c3.4-11.3 13.8-19.1 25.6-19.1s22.2 7.7 25.6 19.1l12.6 42.1c7.1-8.3 17.5-13.1 28.5-13.1c14.2 0 27.2 8 33.5 20.7l5.6 11.3 41.7 0 15.7-62.6c2.1-8.4 6.5-16.1 12.6-22.3L473.5 145.4c18.7-18.7 49.1-18.7 67.9 0l17.4 17.4c18.7 18.7 18.7 49.1 0 67.9L405.1 384.3c-6.2 6.2-13.9 10.5-22.3 12.6l-74.9 18.7c-2 .5-4.1 .6-6.1 .3L240 416c-6.1 0-11.6-3.4-14.3-8.8L215.6 387c-.9-1.8-2.8-3-4.9-3c-1.7 0-3.3 .8-4.4 2.2l-17.6 23.4c-3.6 4.8-9.7 7.2-15.6 6.2s-10.8-5.4-12.5-11.2L144 349zM518.8 168c-6.2-6.2-16.4-6.2-22.6 0l-24.8 24.8 40 40L536.2 208c6.2-6.2 6.2-16.4 0-22.6L518.8 168zM342.5 321.7c-2.1 2.1-3.5 4.6-4.2 7.4l-12.3 49 49-12.3c2.8-.7 5.4-2.2 7.4-4.2L488.7 255.4l-40-40L342.5 321.7z" />
        </svg>
      ),
      active:checkinToday &&
                      checkinToday.checked_out_at &&
                      checkinToday.checked_out_at !== null,
      incomplete: true,
      onPress: () => navigate({ to: '/job/$jobId/$date/signoff', params: { jobId, date } }),
    },
  ];

  if (!booking && !job) {
    return (
      <>
        <div className="flex-1 flex flex-col bg-gray-50 min-h-[calc(100vh-5rem)]">
          <div className="flex-1 flex items-center justify-center">
            <Card className="w-96">
              <CardHeader>
                <CardTitle>Job Not Found</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Could not find job with ID: {jobId}
                </p>
                <Button onClick={goBack} className="w-full">
                  Back to Jobs
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
          <div className="p-8 col-span-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">


            {jobTiles.map((tile) => {
              return (
                <button
                  disabled={!tile.active}
                  data-status={
                    !tile.active
                      ? "inactive"
                      : tile.incomplete
                        ? "incomplete"
                        : "complete"
                  }
                  key={tile.id}
                  className={`group w-full h-full flex flex-col items-center justify-center rounded-lg
                      relative cursor-pointer disabled:cursor-not-allowed transition-all duration-200 disabled:opacity-50 border-2 text-white data-[status=inactive]:border-neutral-700 data-[status=inactive]:bg-neutral-500
                      data-[status=incomplete]:border-amber-500 data-[status=incomplete]:bg-amber-300
                      data-[status=complete]:border-blue-800 data-[status=complete]:bg-blue-600 "
                      ${tile.style || ""}
                    `}
                  onClick={tile.onPress}
                >
                  <div className="p-6 flex-1 flex items-center justify-center">
                    {tile.icon}
                  </div>

                  <div className="w-full py-4 px-6 group-data-[status=inactive]:border-neutral-700 group-data-[status=inactive]:bg-neutral-700 group-data-[status=incomplete]:bg-amber-500 group-data-[status=incomplete]:border-amber-500 group-data-[status=complete]:border-blue-800 group-data-[status=complete]:bg-blue-700 ">
                    <h3 className={`font-medium`}>{tile.name}</h3>
                  </div>
                </button>
              );
            })}

            <button
              className={`w-full h-full flex flex-col items-center justify-center rounded-lg relative cursor-pointer transition-all duration-200 bg-blue-100 border-2 border-blue-600 text-white`}
              onClick={() => {dispatch(updateHelpModal(true))}}
            >
              <div className="p-6 flex-1 flex items-center justify-center">
                <svg
                  className="fill-blue-800 w-16 h-16"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                >
                  <path d="M80 160c0-35.3 28.7-64 64-64l32 0c35.3 0 64 28.7 64 64l0 3.6c0 21.8-11.1 42.1-29.4 53.8l-42.2 27.1c-25.2 16.2-40.4 44.1-40.4 74l0 1.4c0 17.7 14.3 32 32 32s32-14.3 32-32l0-1.4c0-8.2 4.2-15.8 11-20.2l42.2-27.1c36.6-23.6 58.8-64.1 58.8-107.7l0-3.6c0-70.7-57.3-128-128-128l-32 0C73.3 32 16 89.3 16 160c0 17.7 14.3 32 32 32s32-14.3 32-32zm80 320a40 40 0 1 0 0-80 40 40 0 1 0 0 80z" />
                </svg>
              </div>

              <div className="w-full py-4 px-6 rounded-b-lg bg-blue-200 border-blue-600 text-blue-800">
                <h3 className={`font-medium`}>Need Assistance?</h3>
              </div>
              </button>

    </div>
  );
}
